/**
 * "Add your dancers" - the one form a parent fills in to connect their kids
 * to their account. ONE component, used everywhere a parent is asked, so the
 * two signup paths cannot drift into two different questions:
 *
 *   - the waiting-for-approval card, where Google sign-in lands
 *   - /register, where the emailed link lands
 *   - the family home, when an approved account has no dancers on it yet, and
 *     behind "Add another dancer" when a sibling starts later
 *   - nowhere else. The Director links dancers from Manage Users, which
 *     writes the link directly and does not need a claim.
 *
 * Any legacy #482 singular claimedDancer folds in READ-ONLY (see the result
 * list): #482 already owns that one, and letting a parent edit it here would
 * mint a duplicate on rename, since the server drops it from the plural list.
 *
 * What it collects is exactly what Bobbie asked for: the parent's own first
 * and last name, then every dancer with a first name, a last name and a
 * birthday. It scales from one dancer to twelve, because "some parents might
 * have 6 kids some might have 1": one row by default, an obvious one-thumb
 * "Add another dancer", and a wall that explains itself if a family somehow
 * needs more than twelve. The copy says rec AND comp out loud, because a
 * parent who only lists their comp kid leaves the rec kid invisible.
 *
 * The birthday is not paperwork. students.dob is empty for every dancer in
 * the studio today, and a parent typing it is how the system learns it.
 *
 * Nothing here grants access on its own. It posts a CLAIM; the server
 * auto-links a unique name match and records it for the Director to review.
 * See lib/dancerClaims.ts.
 */

import { useEffect, useMemo, useState } from 'react';
import { Icon } from './Icon';
import { useUnsavedWork } from '../lib/useUnsavedWork';
import {
  MAX_CLAIMED_DANCERS,
  MIN_CLAIM_DOB,
  claimStatusTone,
  emptyDancerRow,
  fetchDancerClaim,
  saveMyDancerClaim,
  splitDisplayName,
  type DancerClaim,
  type DancerClaimEntry,
  type DancerClaimInput,
} from '../lib/dancerClaims';

export interface DancerClaimDraft {
  parentFirstName: string;
  parentLastName: string;
  dancers: DancerClaimInput[];
}

export function emptyDancerClaimDraft(displayName?: string | null): DancerClaimDraft {
  const { firstName, lastName } = splitDisplayName(displayName);
  return { parentFirstName: firstName, parentLastName: lastName, dancers: [emptyDancerRow()] };
}

/**
 * Is this draft worth sending? Kept deliberately loose: the server is the
 * authority on what a valid claim is, and it answers in the same warm words
 * this form would. This only decides whether the button is live.
 */
export function draftHasSomething(draft: DancerClaimDraft): boolean {
  return draft.dancers.some((d) => d.firstName.trim() || d.lastName.trim() || d.dob);
}

export function draftIsComplete(draft: DancerClaimDraft): boolean {
  if (!draft.parentFirstName.trim() || !draft.parentLastName.trim()) return false;
  if (draft.dancers.length === 0) return false;
  return draft.dancers.every((d) => d.firstName.trim() && d.lastName.trim() && d.dob);
}

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--ink-soft)',
};

const fieldStyle: React.CSSProperties = {
  font: 'inherit',
  // 16px or bigger, or iOS zooms the whole page in the moment it is focused.
  fontSize: 16,
  padding: '0 var(--space-3)',
  minHeight: 'var(--tap-target)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--white)',
  color: 'var(--ink)',
  width: '100%',
};

const dancerBoxStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 'var(--space-3)',
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-md)',
};

const smallBtnStyle: React.CSSProperties = {
  minHeight: 'var(--tap-target)',
  padding: '0 var(--space-3)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-md)',
  background: 'transparent',
  color: 'var(--ink-soft)',
  font: 'inherit',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const primaryBtnStyle: React.CSSProperties = {
  minHeight: 'var(--tap-target)',
  padding: '0 var(--space-4)',
  border: 0,
  borderRadius: 'var(--radius-md)',
  background: 'var(--btn-primary-bg)',
  color: 'var(--primary-on)',
  font: 'inherit',
  fontSize: 15,
  fontWeight: 800,
  cursor: 'pointer',
  width: '100%',
};

/**
 * The fields on their own, fully controlled. The landing page's Create
 * account form owns them, because there is no account to save against until
 * the password has gone through.
 */
export function DancerClaimFields({
  value,
  onChange,
  disabled = false,
  today,
}: {
  value: DancerClaimDraft;
  onChange: (next: DancerClaimDraft) => void;
  disabled?: boolean;
  /** ISO date used as the birthday ceiling. Defaults to today. */
  today?: string;
}) {
  const maxDob = today ?? new Date().toISOString().slice(0, 10);
  const atCap = value.dancers.length >= MAX_CLAIMED_DANCERS;

  const setDancer = (index: number, patch: Partial<DancerClaimInput>) => {
    onChange({
      ...value,
      dancers: value.dancers.map((d, i) => (i === index ? { ...d, ...patch } : d)),
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={labelStyle}>Your name</span>
        <input
          type="text"
          autoComplete="given-name"
          placeholder="Your first name"
          value={value.parentFirstName}
          onChange={(e) => onChange({ ...value, parentFirstName: e.target.value })}
          style={fieldStyle}
          disabled={disabled}
        />
        <input
          type="text"
          autoComplete="family-name"
          placeholder="Your last name"
          value={value.parentLastName}
          onChange={(e) => onChange({ ...value, parentLastName: e.target.value })}
          style={fieldStyle}
          disabled={disabled}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={labelStyle}>Your dancers</span>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--ink-soft)' }}>
          Add <strong>every</strong> dancer in your family, rec classes and comp team
          both. If you leave one off, we cannot send you their schedule, their music
          or their costume news.
        </p>
      </div>

      {value.dancers.map((dancer, index) => (
        <div key={index} style={dancerBoxStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ ...labelStyle, color: 'var(--lime-deep)' }}>Dancer {index + 1}</span>
            {value.dancers.length > 1 && (
              <button
                type="button"
                style={smallBtnStyle}
                disabled={disabled}
                onClick={() =>
                  onChange({ ...value, dancers: value.dancers.filter((_, i) => i !== index) })
                }
              >
                Remove
              </button>
            )}
          </div>
          <input
            type="text"
            placeholder="Dancer first name"
            value={dancer.firstName}
            onChange={(e) => setDancer(index, { firstName: e.target.value })}
            style={fieldStyle}
            disabled={disabled}
          />
          <input
            type="text"
            placeholder="Dancer last name"
            value={dancer.lastName}
            onChange={(e) => setDancer(index, { lastName: e.target.value })}
            style={fieldStyle}
            disabled={disabled}
          />
          <label
            style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--ink-soft)' }}
          >
            Birthday
            <input
              type="date"
              aria-label={`Dancer ${index + 1} birthday`}
              value={dancer.dob}
              min={MIN_CLAIM_DOB}
              max={maxDob}
              onChange={(e) => setDancer(index, { dob: e.target.value })}
              style={fieldStyle}
              disabled={disabled}
            />
          </label>
          {/* Recorded, not acted on. Ticking this makes no account and sets
              no password: it tells the studio who to set up when dancer
              logins open. The copy says exactly that, because a checkbox
              that looks like it did something is worse than no checkbox. */}
          <label
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              minHeight: 'var(--tap-target)',
              fontSize: 13,
              lineHeight: 1.4,
              color: 'var(--ink)',
              cursor: disabled ? 'default' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={dancer.wantsOwnLogin === true}
              onChange={(e) => setDancer(index, { wantsOwnLogin: e.target.checked })}
              disabled={disabled}
              style={{ width: 20, height: 20, marginTop: 2, flexShrink: 0 }}
            />
            <span>
              This dancer would like their own login one day.
              <br />
              <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                This saves a reminder. After your account is approved and your dancers are
                connected, you choose the username and password in Account.
              </span>
            </span>
          </label>
        </div>
      ))}

      {/* Prominent and one-thumb: a parent of six taps this five times, so it
          is a full-width 44px control with a lime edge, not a faint text link. */}
      {!atCap && (
        <button
          type="button"
          style={{
            minHeight: 'var(--tap-target)',
            width: '100%',
            padding: '0 var(--space-4)',
            border: '1.5px solid var(--lime-deep)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--lime-wash)',
            color: 'var(--lime-deep)',
            font: 'inherit',
            fontSize: 15,
            fontWeight: 800,
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.6 : 1,
          }}
          disabled={disabled}
          onClick={() => onChange({ ...value, dancers: [...value.dancers, emptyDancerRow()] })}
        >
          + Add another dancer
        </button>
      )}
      {atCap && (
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.4,
            color: 'var(--amber-text)',
            background: 'var(--amber-bg)',
            border: '1px solid #e6b800',
            borderRadius: 'var(--radius-md)',
            padding: '8px 10px',
          }}
        >
          That is {MAX_CLAIMED_DANCERS} dancers, the most we can take on one form. If your
          family is even bigger, save these and tell the studio about the rest and they will
          add them for you.
        </div>
      )}
    </div>
  );
}

const TONE_STYLES: Record<'ok' | 'wait' | 'hold', React.CSSProperties> = {
  ok: { background: 'var(--lime-wash)', border: '1px solid var(--lime)', color: 'var(--success-text)' },
  wait: { background: 'var(--bg)', border: '1px solid var(--line)', color: 'var(--ink-soft)' },
  hold: { background: 'var(--amber-bg)', border: '1px solid #e6b800', color: 'var(--amber-text)' },
};

/** What happened to each dancer, in the plain words the server sent back. */
export function ClaimResultList({ dancers }: { dancers: DancerClaimEntry[] }) {
  // The read-only folded singular is shown above the form already; do not
  // repeat it here.
  const rows = dancers.filter((d) => !d.fromSingular);
  if (rows.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map((dancer, index) => (
        <div
          key={`${dancer.firstName}-${dancer.lastName}-${index}`}
          style={{
            ...TONE_STYLES[claimStatusTone(dancer.status)],
            borderRadius: 'var(--radius-md)',
            padding: '8px 10px',
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          <strong>
            {dancer.firstName} {dancer.lastName}
          </strong>
          <br />
          {dancer.summary}
          {dancer.wantsOwnLogin && (
            <>
              <br />
              Own login requested. Set it up from Account after approval and dancer linking.
            </>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The self-contained card: loads whatever this account has already claimed,
 * lets them edit it, saves it.
 *
 * It never says "done" for something that has not happened. A pending
 * account is told plainly that the dancers are saved and waiting on the
 * studio, because the linking really does wait for Kim's approval.
 */
export default function AddDancersCard({
  displayName,
  intro,
  compact = false,
  onSaved,
}: {
  /** Pre-fills the parent name from the account, so it is not typed twice. */
  displayName?: string | null;
  /** Overrides the opening line for the surface this sits on. */
  intro?: string;
  /** Tighter padding for the family home, where it sits inside the page. */
  compact?: boolean;
  onSaved?: (claim: DancerClaim | null) => void;
}) {
  const [draft, setDraft] = useState<DancerClaimDraft>(() => emptyDancerClaimDraft(displayName));
  const [claim, setClaim] = useState<DancerClaim | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchDancerClaim()
      .then((existing) => {
        if (cancelled) return;
        setClaim(existing);
        if (existing && existing.dancers.length > 0) {
          // The legacy #482 singular claim folds in read-only (fromSingular):
          // #482 owns it, and an editable copy here would mint a duplicate on
          // rename because the server drops it from the plural list. So the
          // EDITABLE draft is only the rows the parent can actually change.
          // wantsOwnLogin comes back too, so ticking it for one dancer and
          // returning to add another does not silently wipe it.
          const editable = existing.dancers.filter((d) => !d.fromSingular);
          if (editable.length > 0) {
            setDraft({
              parentFirstName: existing.parentFirstName,
              parentLastName: existing.parentLastName,
              dancers: editable.map((d) => ({
                firstName: d.firstName,
                lastName: d.lastName,
                dob: d.dob,
                wantsOwnLogin: d.wantsOwnLogin === true,
              })),
            });
          }
        }
      })
      .catch(() => {
        // A claim we could not read is not a reason to block a parent from
        // typing one. The save is what matters, and it merges server-side.
        if (cancelled) return;
        setClaim(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Four dancers typed on a phone must survive a stray back-swipe. The old
  // /register form guarded this and the note said new families are "unlikely
  // to try again if it evaporates"; that reason did not go away with the form.
  useUnsavedWork('add-dancers', !saved && draftHasSomething(draft) && !busy);

  const canSave = useMemo(() => draftIsComplete(draft) && !busy, [draft, busy]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await saveMyDancerClaim({
        parentFirstName: draft.parentFirstName,
        parentLastName: draft.parentLastName,
        dancers: draft.dancers,
      });
      setClaim(result.claim);
      setSaved(true);
      setNotice(
        result.applied
          ? 'Saved. Here is where each dancer landed.'
          : result.message ??
              'Saved. Your dancers connect as soon as the studio approves your account.'
      );
      onSaved?.(result.claim);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        padding: compact ? 'var(--space-4)' : 'var(--space-5)',
        background: 'var(--paper)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-md)',
        width: '100%',
        textAlign: 'left',
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontWeight: 800,
          fontSize: 15,
          color: 'var(--ink)',
        }}
      >
        <Icon name="people" size={20} />
        Add your dancers
      </div>
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--ink-soft)' }}>
        {intro ??
          'Tell us who dances with us so we can send you the right schedules, music and comp news.'}
      </p>

      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>Checking what we have...</div>
      ) : (
        <>
          {/* The legacy #482 singular dancer, shown read-only: it is already
              submitted and #482 owns it, so the parent sees it here without an
              editable row whose edits would evaporate. */}
          {claim && claim.dancers.some((d) => d.fromSingular) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={labelStyle}>Already submitted</span>
              {claim.dancers
                .filter((d) => d.fromSingular)
                .map((d, i) => (
                  <div
                    key={`${d.firstName}-${d.lastName}-${i}`}
                    style={{
                      fontSize: 13,
                      color: 'var(--ink)',
                      background: 'var(--bg)',
                      border: '1px solid var(--line)',
                      borderRadius: 'var(--radius-md)',
                      padding: '8px 10px',
                    }}
                  >
                    {d.firstName} {d.lastName}
                  </div>
                ))}
            </div>
          )}
          <DancerClaimFields
            value={draft}
            onChange={(next) => {
              setSaved(false);
              setDraft(next);
            }}
            disabled={busy}
          />
          <button type="button" style={{ ...primaryBtnStyle, opacity: canSave ? 1 : 0.6 }} disabled={!canSave} onClick={save}>
            {busy ? 'Saving...' : 'Save my dancers'}
          </button>
          {!draftIsComplete(draft) && draftHasSomething(draft) && (
            <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
              Each dancer needs a first name, a last name and a birthday.
            </div>
          )}
          {notice && (
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.4,
                color: 'var(--success-text)',
                background: 'var(--lime-wash)',
                border: '1px solid var(--lime)',
                borderRadius: 'var(--radius-md)',
                padding: '8px 10px',
              }}
            >
              {notice}
            </div>
          )}
          {error && (
            <div style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--rose-deep)' }}>{error}</div>
          )}
          {claim && claim.dancers.length > 0 && <ClaimResultList dancers={claim.dancers} />}
        </>
      )}
    </div>
  );
}
